import GeofenceRequester from '@cobliteam/api-lib/src/dashboard/geofence/GeofenceRequesterV3';
import { Environment } from '@cobliteam/react-environments';
import { useQuery } from '@tanstack/react-query';

// Mirrors modules/geofences/src/pages/GeofencesPage/Geofences.tsx's hasFewGeofences (also reused,
// with the same threshold, by modules/routing's ServicesDeliveriesSuggestionsBanner).
export const FEW_GEOFENCES_MAX = 2;
export const hasFewGeofences = (geofenceCount: number): boolean => geofenceCount < FEW_GEOFENCES_MAX;

const STALE_TIME_MS = 10 * 60 * 1000;

// Lazy instantiation (same pattern as useEntity/useGeofenceV3) to avoid circular dependency issues
// and to keep Environment access out of module load time.
const getGeofenceRequester = () => new GeofenceRequester({ host: Environment.getInstance().getEnvironment().geofenceApiHost });

/**
 * AE-2569: cheap fleet-wide geofence-count check backing the "flag OR under-threshold" fallback for
 * geofence/location AI suggestions in the floating card (a fleet without `poisRecommendations`/
 * `aiRecommendations2` should still see geofence suggestions while it has fewer than 2 geofences).
 *
 * Only fetches when `enabled` — callers that already have flag-based access should pass `false` to
 * skip the extra request.
 */
const useHasFewGeofences = (fleetId: string | undefined, enabled = true) => {
  const { data, isLoading } = useQuery({
    queryKey: ['AI_RECOMMENDATIONS_GEOFENCE_COUNT', fleetId],
    queryFn: async () => {
      const response = await getGeofenceRequester().getGeofences({ filters: { page: 0, unpaged: true } });
      return response.content;
    },
    enabled: !!fleetId && enabled,
    staleTime: STALE_TIME_MS,
    refetchOnWindowFocus: false,
    retry: false,
  });

  return { isEligible: hasFewGeofences(data?.length ?? 0), isLoading };
};

export default useHasFewGeofences;
